import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  Video, Loader2, Users, Search, X, CheckCircle, Clock,
  ChevronDown, ChevronUp, Unlock, Filter, Zap, Check, ArrowRight, Archive
} from 'lucide-react';
import AdminLiveSessionManager from '../components/AdminLiveSessionManager';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const COURSE_SHORT_NAMES = {
  'course-pdl-weiterbildung-teil1': 'PDL Teil 1',
  'course-pdl-weiterbildung-teil2': 'PDL Teil 2',
  'course-weiterbildung-wundexperte': 'Wundtherapeut',
  'course-wundexperte-84': 'Wundexperte',
  'course-behandlungspflege-lg': 'Behandlungspfl.',
  'course-wundmanagement': 'Wunden (Leitung)',
  'course-wundmanagement-pflegefachkraefte': 'Wunden (Pflege)',
  'course-wund-rezertifizierung': 'Wund-Rezert.',
  'course-palliativpflege': 'Palliativ',
  'course-praxisanleiter': 'Praxisanleiter',
  'course-pflegeberater': 'Pflegeberater',
  'course-hygienebeauftragter': 'Hygienebeauftragter',
  'course-gerontopsychiatrie': 'Gerontopsychiatrie',
  'course-qualitaetsbeauftragter': 'Qualitätsbeauftragter',
  'course-pflegehilfskraft': 'Pflegehilfskraft m. Betr.',
  'course-rezertifizierung-43b': 'Rezert. 43b/53b',
  'course-externenpruefung-pfa': 'Externenpr. PFA',
};

const getShortName = (courseId, title) => COURSE_SHORT_NAMES[courseId] || (title ? title.substring(0, 15) : courseId);

// Bug-Fix: Modultitel enthalten teils schon "Modul X:" — Präfix strippen, sonst doppelt
const cleanModuleTitle = (t) => (t || '').replace(/^Modul\s*\d+\s*:\s*/i, '');

const AdminLiveSessionsPage = ({ Layout, getAuthHeader }) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingSessions, setPendingSessions] = useState({ pending_count: 0, participants: [] });
  const [loadingPending, setLoadingPending] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('queue'); // 'queue' or 'course'
  const [quickUnlocking, setQuickUnlocking] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set()); // composite keys: user_id|module_id
  const [bulkLoading, setBulkLoading] = useState(false);
  const [removingKeys, setRemovingKeys] = useState(new Set());
  const [includeArchived, setIncludeArchived] = useState(false);
  // Server-seitige Paginierung + Suche (Warteschlange)
  const [page, setPage] = useState(1);
  const [pendingMeta, setPendingMeta] = useState({ total_pages: 1, total_users: 0 });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Globale Teilnehmer-Suche (über alle Kurse) — größter UX-Painpoint
  const [allUsers, setAllUsers] = useState([]);
  const [globalSearch, setGlobalSearch] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [initialExpandedUser, setInitialExpandedUser] = useState(null);

  const COURSES_WITH_LEARNING_SYSTEM = [
    'course-pdl-weiterbildung-teil1',
    'course-pdl-weiterbildung-teil2',
    'course-weiterbildung-wundexperte',   // 168 UE — alias "Wundtherapeut"
    'course-wundexperte-84',              // 84 UE — der neue Wundexperte
    'course-behandlungspflege-lg',
    'course-wundmanagement',
    'course-wundmanagement-pflegefachkraefte',
    'course-wund-rezertifizierung',
    'course-palliativpflege',
    'course-praxisanleiter',
    'course-pflegeberater',
    'course-hygienebeauftragter',
    'course-gerontopsychiatrie',
    'course-qualitaetsbeauftragter',
    'course-pflegehilfskraft',            // "Pflegehilfskraft mit Betreuung"
    'course-rezertifizierung-43b',
    'course-externenpruefung-pfa',
  ];

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suche debouncen — die eigentliche Suche läuft in der DB (Backend-Param), nicht im Client
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchQuery.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived, page, debouncedSearch]);

  const loadPending = async () => {
    setLoadingPending(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '20' });
      if (includeArchived) params.set('include_archived', 'true');
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res = await axios.get(`${API}/admin/pending-sessions?${params.toString()}`, getAuthHeader());
      setPendingSessions(res.data);
      setPendingMeta({ total_pages: res.data.total_pages || 1, total_users: res.data.total_users || 0 });
    } catch {
      setPendingSessions({ pending_count: 0, participants: [] });
    }
    setLoadingPending(false);
  };

  const loadData = async () => {
    try {
      const [coursesRes, usersRes] = await Promise.all([
        axios.get(`${API}/courses`),
        axios.get(`${API}/admin/users?include_archived=all`, getAuthHeader()).catch(() => ({ data: [] })),
      ]);
      const learningCourses = coursesRes.data.filter(c =>
        c.learning_system === true || COURSES_WITH_LEARNING_SYSTEM.includes(c.id)
      );
      setCourses(learningCourses);
      // Nur User, die mindestens 1 learning-system Kurs zugewiesen haben
      const lsCourseIds = new Set(learningCourses.map(c => c.id));
      const enrichedUsers = (usersRes.data || [])
        .filter(u => u.role !== 'admin')
        .map(u => ({
          ...u,
          learning_course_ids: (u.assigned_courses || []).filter(cid => lsCourseIds.has(cid)),
        }))
        .filter(u => u.learning_course_ids.length > 0);
      setAllUsers(enrichedUsers);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  // Quick approve from queue
  const quickApprove = async (participant) => {
    setQuickUnlocking(participant.module_id + participant.user_id);
    try {
      await axios.post(
        `${API}/admin/live-sessions/approve?user_id=${participant.user_id}&module_id=${participant.module_id}&notes=Schnellfreigabe&session_date=${new Date().toISOString().split('T')[0]}&session_duration_minutes=45`,
        {},
        getAuthHeader()
      );
      toast.success(`${participant.user_name}: ${cleanModuleTitle(participant.module_title)} freigegeben!`);
      removeWithAnimation([keyFor(participant)]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler bei der Freigabe");
    }
    setQuickUnlocking(null);
  };

  // Zeile(n) sanft rausrutschen lassen, dann Liste + Sidebar-Zähler aktualisieren
  const removeWithAnimation = (keys) => {
    setRemovingKeys(prev => new Set([...prev, ...keys]));
    setTimeout(() => {
      setPendingSessions(prev => ({
        pending_count: Math.max(0, (prev.pending_count || 0) - keys.length),
        participants: (prev.participants || []).filter(p => !keys.includes(`${p.user_id}|${p.module_id}`)),
      }));
      setRemovingKeys(prev => { const n = new Set(prev); keys.forEach(k => n.delete(k)); return n; });
      window.dispatchEvent(new CustomEvent('dh-pending-sessions-changed'));
    }, 350);
  };

  const rejectSession = async (participant) => {
    if (!window.confirm(`Freigabe für ${participant.user_name} — „${cleanModuleTitle(participant.module_title)}" ablehnen?`)) return;
    try {
      await axios.post(
        `${API}/admin/live-sessions/reject?user_id=${participant.user_id}&module_id=${participant.module_id}&notes=Abgelehnt`,
        {},
        getAuthHeader()
      );
      toast.success('Freigabe abgelehnt');
      removeWithAnimation([keyFor(participant)]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ablehnen');
    }
  };

  const bulkReject = async () => {
    if (selectedKeys.size === 0) return;
    if (!window.confirm(`${selectedKeys.size} Freigaben wirklich ablehnen?`)) return;
    setBulkLoading(true);
    const toReject = filteredPending.filter(m => selectedKeys.has(keyFor(m)));
    let success = 0, failed = 0;
    for (const m of toReject) {
      try {
        await axios.post(`${API}/admin/live-sessions/reject?user_id=${m.user_id}&module_id=${m.module_id}&notes=Sammelablehnung`, {}, getAuthHeader());
        success++;
      } catch { failed++; }
    }
    if (success > 0) toast.success(`${success} Freigabe(n) abgelehnt.`);
    if (failed > 0) toast.error(`${failed} Fehler.`);
    removeWithAnimation(toReject.map(keyFor));
    setSelectedKeys(new Set());
    setBulkLoading(false);
  };
  
  // Filter Users for global search (name/username, tolerant)
  const globalSearchResults = useMemo(() => {
    const q = globalSearch.trim().toLowerCase();
    if (q.length < 2) return [];
    return allUsers
      .filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.username || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [globalSearch, allUsers]);

  const jumpToParticipant = (user) => {
    const courseIds = user.learning_course_ids || [];
    if (courseIds.length === 0) {
      toast.warning(`${user.name} hat keinen Lern-System-Kurs zugewiesen.`);
      return;
    }
    let targetCourseId = courseIds[0];
    if (courseIds.length > 1) {
      // Falls mehrere Kurse: nimm den mit den meisten offenen Live-Sessions in der Queue,
      // sonst einfach den ersten. Später kann man das als richtiges Chooser-Modal bauen.
      const pendingByCourse = {};
      (pendingSessions.participants || []).forEach(p => {
        if (p.user_id === user.id) {
          pendingByCourse[p.course_id] = (pendingByCourse[p.course_id] || 0) + 1;
        }
      });
      const withPending = courseIds
        .map(cid => [cid, pendingByCourse[cid] || 0])
        .sort((a, b) => b[1] - a[1]);
      targetCourseId = withPending[0][0];
    }
    const targetCourse = courses.find(c => c.id === targetCourseId);
    if (!targetCourse) {
      toast.error('Kurs nicht mehr verfügbar');
      return;
    }
    setViewMode('course');
    setSelectedCourse(targetCourse);
    setInitialExpandedUser(user.id);
    setSearchQuery(user.name || user.username || '');
    setGlobalSearch('');
    setShowSearchDropdown(false);
  };

  // Composite key helper
  const keyFor = (m) => `${m.user_id}|${m.module_id}`;
  
  const toggleSelect = (m) => {
    const k = keyFor(m);
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  
  const selectAllForUser = (modules) => {
    const allSelected = modules.every(m => selectedKeys.has(keyFor(m)));
    setSelectedKeys(prev => {
      const next = new Set(prev);
      modules.forEach(m => {
        if (allSelected) next.delete(keyFor(m));
        else next.add(keyFor(m));
      });
      return next;
    });
  };
  
  const selectAllPending = () => {
    if (selectedKeys.size === filteredPending.length) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(filteredPending.map(keyFor)));
    }
  };
  
  // Bulk approve selected
  const bulkApprove = async () => {
    if (selectedKeys.size === 0) return;
    if (!window.confirm(`${selectedKeys.size} Modul-Freigaben gleichzeitig bestätigen?`)) return;
    setBulkLoading(true);
    const today = new Date().toISOString().split('T')[0];
    const toApprove = filteredPending.filter(m => selectedKeys.has(keyFor(m)));
    let success = 0, failed = 0;
    for (const m of toApprove) {
      try {
        await axios.post(
          `${API}/admin/live-sessions/approve?user_id=${m.user_id}&module_id=${m.module_id}&notes=Sammelfreigabe&session_date=${today}&session_duration_minutes=45`,
          {},
          getAuthHeader()
        );
        success++;
      } catch {
        failed++;
      }
    }
    if (success > 0) toast.success(`${success} Modul(e) freigegeben.`);
    if (failed > 0) toast.error(`${failed} Fehler bei der Freigabe.`);
    removeWithAnimation(toApprove.map(keyFor));
    setSelectedKeys(new Set());
    setBulkLoading(false);
  };

  // Serverseitig gefiltert + paginiert — Client filtert nicht mehr nach
  const filteredPending = pendingSessions.participants || [];

  // Group pending by user
  const pendingGrouped = useMemo(() => {
    const groups = {};
    filteredPending.forEach(p => {
      if (!groups[p.user_id]) {
        groups[p.user_id] = {
          user_name: p.user_name,
          username: p.username,
          archived: !!p.archived,
          archived_at: p.archived_at,
          archived_by: p.archived_by,
          modules: [],
        };
      }
      groups[p.user_id].modules.push(p);
    });
    return Object.entries(groups).sort((a, b) => a[1].user_name.localeCompare(b[1].user_name));
  }, [filteredPending]);

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-terra" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto animate-fade-in">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-8 h-[2px] bg-terra" />
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-terra">Administration · Live-Sessions</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-espresso leading-[1.05]">
            Frei<span className="italic text-terra">gaben</span>.
          </h1>
        </div>

        {/* KPI-Row + Globale Teilnehmer-Suche */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_auto_auto] gap-3 mb-4">
          {/* Global search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-sand-500 z-10" />
            <input
              type="text"
              value={globalSearch}
              onChange={(e) => { setGlobalSearch(e.target.value); setShowSearchDropdown(true); }}
              onFocus={() => setShowSearchDropdown(true)}
              onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
              placeholder="🔍 Teilnehmer suchen — direkt zum Kurs springen (Name, Username, E-Mail)…"
              className="w-full pl-11 pr-4 py-3 bg-cream-100 border border-cream-300 rounded-xl text-sand-800 placeholder-sand-500 focus:outline-none focus:border-terra focus:ring-2 focus:ring-terra/20 text-sm"
              data-testid="global-participant-search"
            />
            {showSearchDropdown && globalSearchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1.5 bg-cream-50 border border-cream-300 rounded-xl shadow-hover z-30 overflow-hidden">
                {globalSearchResults.map(user => {
                  const userCourses = user.learning_course_ids
                    .map(cid => courses.find(c => c.id === cid))
                    .filter(Boolean);
                  const pendingCount = (pendingSessions.participants || []).filter(p => p.user_id === user.id).length;
                  return (
                    <button
                      key={user.id}
                      onMouseDown={(e) => { e.preventDefault(); jumpToParticipant(user); }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-cream-100 transition-colors text-left border-b border-cream-300 last:border-0"
                      data-testid={`jump-user-${user.id}`}
                    >
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-cream-50 font-bold text-xs shrink-0 ${user.archived ? 'bg-sand-500' : 'bg-gradient-to-br from-terra to-terra-dark'}`}>
                        {(user.name || user.username || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-sand-800 truncate flex items-center gap-2">
                          {user.name || user.username}
                          {user.archived && <span className="text-[9px] px-1.5 py-0.5 rounded bg-sand-800 text-cream-50 font-bold uppercase">Archiviert</span>}
                          {pendingCount > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold">{pendingCount} offen</span>}
                        </div>
                        <div className="text-[11px] text-sand-500 truncate">
                          {userCourses.length} Kurs{userCourses.length !== 1 ? 'e' : ''}: {userCourses.map(c => c.title).slice(0, 2).join(' · ')}{userCourses.length > 2 ? ` + ${userCourses.length - 2}` : ''}
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-terra shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
            {showSearchDropdown && globalSearch.trim().length >= 2 && globalSearchResults.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-1.5 bg-cream-50 border border-cream-300 rounded-xl shadow-hover z-30 px-4 py-3 text-sm text-sand-500">
                Kein Teilnehmer gefunden zu &bdquo;{globalSearch}&ldquo;
              </div>
            )}
          </div>
          {/* KPI-Kacheln */}
          <div className="px-4 py-3 bg-cream-100 border border-cream-300 rounded-xl min-w-[130px]">
            <div className="text-[10px] uppercase tracking-wider text-sand-500 font-semibold">Offene Freigaben</div>
            <div className={`font-display text-2xl mt-0.5 ${pendingSessions.pending_count > 0 ? 'text-terra' : 'text-sand-800'}`}>{pendingSessions.pending_count || 0}</div>
          </div>
          <div className="px-4 py-3 bg-cream-100 border border-cream-300 rounded-xl min-w-[130px]">
            <div className="text-[10px] uppercase tracking-wider text-sand-500 font-semibold">Teilnehmer</div>
            <div className="font-display text-2xl text-sand-800 mt-0.5">{allUsers.filter(u => !u.archived).length}</div>
          </div>
          <div className="px-4 py-3 bg-cream-100 border border-cream-300 rounded-xl min-w-[130px]">
            <div className="text-[10px] uppercase tracking-wider text-sand-500 font-semibold">Aktive Kurse</div>
            <div className="font-display text-2xl text-sand-800 mt-0.5">{courses.length}</div>
          </div>
        </div>

        {/* View Toggle + Search */}
        <div className="bg-cream-100 rounded-xl border border-cream-300 p-3 mb-4 flex flex-col md:flex-row items-start md:items-center gap-3">
          {/* View toggle */}
          <div className="flex bg-cream-50 rounded-lg p-1 border border-cream-300" data-testid="session-view-toggle">
            <button
              onClick={() => { setViewMode('queue'); setSearchQuery(''); }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${viewMode === 'queue' ? 'bg-terra text-sand-800' : 'text-sand-500 hover:text-sand-800'}`}
            >
              <Zap className="w-3.5 h-3.5" />
              Warteschlange
              {pendingSessions.pending_count > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{pendingSessions.pending_count}</span>
              )}
            </button>
            <button
              onClick={() => { setViewMode('course'); setSearchQuery(''); }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${viewMode === 'course' ? 'bg-terra text-sand-800' : 'text-sand-500 hover:text-sand-800'}`}
            >
              <Users className="w-3.5 h-3.5" />
              Nach Kurs
            </button>
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sand-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={viewMode === 'queue' ? "Teilnehmer oder Modul suchen..." : "Teilnehmer suchen..."}
              className="w-full pl-9 pr-8 py-2 bg-cream-50 border border-cream-300 rounded-lg text-sand-800 text-sm placeholder:text-sand-400 focus:outline-none focus:border-terra"
              data-testid="session-search"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-sand-500 hover:text-sand-800">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Jump-to-Benutzerverwaltung: lets admin search ALL participants */}
          <button
            type="button"
            onClick={() => navigate(`/admin/users${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ''}`)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-sand-700 bg-cream-50 border border-cream-300 hover:bg-cream-200 hover:text-sand-800 transition-colors"
            title="Alle Teilnehmer in der Benutzerverwaltung durchsuchen"
            data-testid="search-all-participants"
          >
            <Users className="w-3.5 h-3.5 text-terra" />
            Alle Teilnehmer
            <ArrowRight className="w-3 h-3 opacity-60" />
          </button>

          {/* Toggle: Auch archivierte Teilnehmer anzeigen */}
          <button
            type="button"
            onClick={() => setIncludeArchived(v => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
              includeArchived
                ? 'bg-sand-800 text-cream-50 border-sand-800 hover:bg-espresso'
                : 'bg-cream-50 text-sand-700 border-cream-300 hover:bg-cream-200 hover:text-sand-800'
            }`}
            title={includeArchived ? 'Archivierte Teilnehmer werden eingeblendet — klicken um auszublenden' : 'Archivierte Teilnehmer einblenden'}
            data-testid="toggle-include-archived"
          >
            <Archive className="w-3.5 h-3.5" />
            {includeArchived ? 'Auch archivierte' : 'Nur aktive'}
          </button>

          {/* Course selector (only in course view) */}
          {viewMode === 'course' && (
            <select
              value={selectedCourse?.id || ''}
              onChange={(e) => setSelectedCourse(courses.find(c => c.id === e.target.value) || null)}
              className="px-3 py-2 bg-cream-50 border border-cream-300 rounded-lg text-sand-800 text-sm focus:outline-none focus:border-terra max-w-[320px]"
              title={selectedCourse?.title || 'Kurs auswählen'}
              data-testid="course-select"
            >
              <option value="">Kurs auswählen...</option>
              {courses.map(c => (
                <option key={c.id} value={c.id} title={c.title}>{c.title}</option>
              ))}
            </select>
          )}
        </div>

        {/* ============ QUEUE VIEW ============ */}
        {viewMode === 'queue' && (
          <div className="bg-cream-100 rounded-2xl border border-cream-300 overflow-hidden">
            {loadingPending ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-terra" /></div>
            ) : filteredPending.length === 0 ? (
              <div className="text-center py-16 text-sand-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400/50" />
                <p className="text-lg font-medium text-sand-500">Alles erledigt!</p>
                <p className="text-sm mt-1">Keine wartenden Freigaben{searchQuery ? ' für diese Suche' : ''}.</p>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/users${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ''}`)}
                  className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-sand-800 bg-cream-50 border border-cream-300 hover:bg-cream-200 transition-colors"
                  data-testid="empty-state-search-users"
                >
                  <Users className="w-4 h-4 text-terra" />
                  {searchQuery ? `Teilnehmer „${searchQuery}" in Benutzerverwaltung suchen` : 'Alle Teilnehmer durchsuchen'}
                  <ArrowRight className="w-3.5 h-3.5 opacity-60" />
                </button>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="px-4 py-3 bg-cream-50 border-b border-cream-300 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={filteredPending.length > 0 && selectedKeys.size === filteredPending.length}
                      onChange={selectAllPending}
                      className="w-4 h-4 rounded bg-cream-200 border-cream-300 accent-[#D4C4B0] cursor-pointer"
                      title="Alle auswählen"
                      data-testid="select-all-pending"
                    />
                    <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                    <span className="text-sm font-medium text-sand-800">
                      {filteredPending.length} Module · {pendingGrouped.length} Teilnehmer
                      {includeArchived && pendingGrouped.some(([_, d]) => d.archived) && (
                        <span className="ml-2 text-sand-500">
                          · davon {pendingGrouped.filter(([_, d]) => d.archived).length} archiviert
                        </span>
                      )}
                      {selectedKeys.size > 0 && <span className="ml-2 text-amber-300">· {selectedKeys.size} ausgewählt</span>}
                    </span>
                  </div>
                  {selectedKeys.size > 0 && (
                    <div className="flex items-center gap-3" data-testid="bulk-bar">
                      <span className="font-mono text-[10.5px] uppercase font-semibold text-sand-600" style={{ letterSpacing: '0.14em' }}>
                        {selectedKeys.size} ausgewählt
                      </span>
                      <span style={{ color: '#8F650F' }}>·</span>
                      <button
                        onClick={bulkApprove}
                        disabled={bulkLoading}
                        className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold uppercase transition-colors disabled:opacity-50"
                        style={{ border: '1.5px solid #E07A5F', color: '#E07A5F', letterSpacing: '0.12em' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E07A5F'; e.currentTarget.style.color = '#F5F0EA'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#E07A5F'; }}
                        data-testid="bulk-approve"
                      >
                        {bulkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Freigeben
                      </button>
                      <button
                        onClick={bulkReject}
                        disabled={bulkLoading}
                        className="text-xs font-semibold text-sand-600 hover:text-terra transition-colors disabled:opacity-50"
                        data-testid="bulk-reject"
                      >
                        Ablehnen
                      </button>
                    </div>
                  )}
                </div>

                {/* Grouped by user */}
                {pendingGrouped.map(([userId, data]) => {
                  const allUserSelected = data.modules.every(m => selectedKeys.has(keyFor(m)));
                  return (
                  <div key={userId} className={`border-b border-cream-200 last:border-0 ${data.archived ? 'opacity-70 bg-cream-50/40' : ''}`}>
                    <div className="px-4 py-3 flex items-start gap-3">
                      {/* Group select */}
                      <input
                        type="checkbox"
                        checked={allUserSelected}
                        onChange={() => selectAllForUser(data.modules)}
                        className="mt-1.5 w-4 h-4 rounded bg-cream-200 border-cream-300 accent-[#D4C4B0] cursor-pointer flex-shrink-0"
                        title={`Alle ${data.modules.length} Module von ${data.user_name} auswählen`}
                      />
                      {/* User avatar */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-cream-50 font-bold text-xs flex-shrink-0 mt-0.5 ${data.archived ? 'bg-sand-500' : 'bg-gradient-to-br from-terra to-terra-dark'}`}>
                        {data.user_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="text-sm font-medium text-sand-800 flex items-center gap-2">
                            {data.user_name}
                            <span className="text-[10px] text-sand-500">({data.modules.length} Module)</span>
                            {data.archived && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sand-800 text-cream-50 text-[10px] font-bold tracking-wide uppercase"
                                title={data.archived_at ? `Archiviert am ${new Date(data.archived_at).toLocaleDateString('de-DE')}${data.archived_by ? ` durch ${data.archived_by}` : ''}` : 'Archiviert'}
                                data-testid={`archived-badge-${userId}`}
                              >
                                <Archive className="w-2.5 h-2.5" />
                                Archiviert
                              </span>
                            )}
                          </p>
                        </div>
                        
                        {/* Modules to approve */}
                        <div className="mt-2 space-y-1.5">
                          {data.modules.map(m => {
                            const isSelected = selectedKeys.has(keyFor(m));
                            return (
                            <div key={m.module_id} className={`flex items-center justify-between gap-2 p-2 rounded-lg border transition-all duration-300 ${removingKeys.has(keyFor(m)) ? 'opacity-0 translate-x-8' : ''} ${isSelected ? 'bg-terra/5 border-terra/30' : 'bg-cream-50 border-cream-200'}`}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelect(m)}
                                className="w-3.5 h-3.5 rounded bg-cream-200 border-cream-300 accent-[#D4C4B0] cursor-pointer flex-shrink-0"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-cream-200 text-sand-500 border border-cream-300">{getShortName(m.course_id, m.course_title)}</span>
                                  <span className="text-xs text-sand-800 truncate">Modul {m.module_order}: {cleanModuleTitle(m.module_title)}</span>
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-[10.5px]">
                                  {m.time_accumulated_minutes >= m.time_required_minutes ? (
                                    <span className="flex items-center gap-1" style={{ color: '#8F650F' }} title={`${m.time_accumulated_minutes}/${m.time_required_minutes} Min`}>
                                      Lernzeit erfüllt <Check className="w-3 h-3" />
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-0.5 text-sand-500" title="Aufgezeichnete Lernzeit / Soll"><Clock className="w-2.5 h-2.5" />{m.time_accumulated_minutes}/{m.time_required_minutes} Min</span>
                                  )}
                                  <span className="flex items-center gap-1" style={{ color: '#8F650F' }} title={`Quiz-Ergebnis: ${Math.round(m.quiz_score)} %`}>
                                    Quiz bestanden <Check className="w-3 h-3" />
                                  </span>
                                </div>
                              </div>
                              
                              {/* Freigeben (Terra-Outline) + Ablehnen */}
                              <div className="flex items-center gap-2.5 flex-shrink-0">
                                <button
                                  onClick={() => quickApprove(m)}
                                  disabled={quickUnlocking === m.module_id + m.user_id}
                                  className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase transition-colors disabled:opacity-50"
                                  style={{ border: '1.5px solid #E07A5F', color: '#E07A5F', letterSpacing: '0.1em' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E07A5F'; e.currentTarget.style.color = '#F5F0EA'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#E07A5F'; }}
                                  data-testid={`quick-approve-${m.user_id}-${m.module_id}`}
                                >
                                  {quickUnlocking === m.module_id + m.user_id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Unlock className="w-3 h-3" />
                                  )}
                                  Freigeben
                                </button>
                                <button
                                  onClick={() => rejectSession(m)}
                                  className="text-[11px] text-sand-500 hover:text-terra transition-colors"
                                  data-testid={`reject-${m.user_id}-${m.module_id}`}
                                >
                                  Ablehnen
                                </button>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}

                {/* Paginierung */}
                {pendingMeta.total_pages > 1 && (
                  <div className="px-4 py-3 bg-cream-50 border-t border-cream-300 flex items-center justify-between gap-3" data-testid="pending-pagination">
                    <span className="text-[12px] text-sand-500">
                      Seite {pendingSessions.page || page} von {pendingMeta.total_pages} · {pendingMeta.total_users} Teilnehmer gesamt
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={(pendingSessions.page || page) <= 1 || loadingPending}
                        className="px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border border-cream-300 text-sand-700 hover:bg-cream-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        data-testid="pending-prev-page"
                      >
                        Zurück
                      </button>
                      <button
                        onClick={() => setPage(p => Math.min(pendingMeta.total_pages, p + 1))}
                        disabled={(pendingSessions.page || page) >= pendingMeta.total_pages || loadingPending}
                        className="px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border border-terra text-terra hover:bg-terra hover:text-cream-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        data-testid="pending-next-page"
                      >
                        Weiter
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ============ COURSE VIEW ============ */}
        {viewMode === 'course' && (
          <>
            {!selectedCourse ? (
              <div className="bg-cream-100 rounded-2xl border border-cream-300 p-12 text-center text-sand-500">
                <Video className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Bitte einen Kurs auswählen</p>
              </div>
            ) : (
              <AdminLiveSessionManager
                key={selectedCourse.id + (includeArchived ? '-archived' : '') + (initialExpandedUser || '')}
                courseId={selectedCourse.id}
                courseName={selectedCourse.title}
                getAuthHeader={getAuthHeader}
                includeArchived={includeArchived}
                initialExpandedUser={initialExpandedUser}
              />
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminLiveSessionsPage;
